<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         showTable: false,
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
      }
   },
   computed: {
      ...mapState({
         kbliPerSkala: state => state.DashboardOss.kbliPerSkala,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      chartData() {
         return this.kbliPerSkala.data || {}
      },
      chartSeries() {
         return [
            {
               name: 'Jumlah KBLI',
               data: [
                  this.chartData.mikro || 0,
                  this.chartData.kecil || 0,
                  this.chartData.menengah || 0,
                  this.chartData.besar || 0,
               ],
            },
         ]
      },
      tableItems() {
         const data = this.kbliPerSkala.data || {}
         const mapping = [
            { key: 'mikro', label: 'Mikro' },
            { key: 'kecil', label: 'Kecil' },
            { key: 'menengah', label: 'Menengah' },
            { key: 'besar', label: 'Besar' },
         ]
         return mapping.map(item => ({
            skala_usaha: item.label,
            total: data[item.key] || 0,
         }))
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'
         const gridColor = this.isDark ? '#3b4253' : '#ebe9f1'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: ['#9f1e15'],
            plotOptions: {
               bar: {
                  borderRadius: 4,
                  columnWidth: '50%',
                  distributed: true,
               },
            },
            dataLabels: {
               enabled: true,
               formatter(val) {
                  return val > 0 ? val.toLocaleString('id-ID') : ''
               },
            },
            grid: {
               borderColor: gridColor,
               strokeDashArray: 4,
            },
            legend: {
               show: false,
            },
            xaxis: {
               categories: ['Mikro', 'Kecil', 'Menengah', 'Besar'],
               labels: {
                  style: {
                     colors: textColor,
                  },
               },
               title: {
                  text: 'Skala Usaha',
                  style: {
                     color: textColor,
                  },
               },
            },
            yaxis: {
               min: 0,
               forceNiceScale: true,
               title: {
                  text: 'Jumlah KBLI',
                  style: {
                     color: textColor,
                  },
               },
               labels: {
                  style: {
                     colors: [textColor],
                  },
                  formatter: value => Math.round(value).toLocaleString('id-ID'),
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${value.toLocaleString('id-ID')} KBLI`,
               },
            },
         }
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getKbliPerSkala: 'DashboardOss/getKbliPerSkala',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getKbliPerSkala(year)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        KBLI per Skala Usaha tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Distribusi jumlah KBLI berdasarkan skala usaha</small>
                  </div>
                  <b-button-group size="sm">
                     <b-button
                        :variant="!showTable ? 'primary' : 'outline-secondary'"
                        @click="showTable = false"
                     >
                        <feather-icon icon="BarChart2Icon" size="14" class="mr-25" />
                        Chart
                     </b-button>
                     <b-button
                        :variant="showTable ? 'primary' : 'outline-secondary'"
                        @click="showTable = true"
                     >
                        <feather-icon icon="TableIcon" size="14" class="mr-25" />
                        Tabel
                     </b-button>
                  </b-button-group>
               </div>

               <!-- Chart View -->
               <div v-if="!showTable">
                  <vue-apex-charts
                     type="bar"
                     height="300"
                     :options="chartOptions"
                     :series="chartSeries"
                  />
               </div>

               <!-- Table View -->
               <div v-else>
                  <b-table
                     :items="tableItems"
                     :fields="[
                        { key: 'skala_usaha', label: 'Skala Usaha' },
                        { key: 'total', label: 'Jumlah KBLI' },
                     ]"
                     responsive
                     class="kbli-table"
                  >
                     <template #cell(total)="data">
                        <span class="font-weight-bolder">
                           {{ data.item.total.toLocaleString('id-ID') }}
                        </span>
                     </template>
                  </b-table>
               </div>
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.summary-card {
   background: #fff5f4;
   display: inline-block;
   min-width: 120px;
   transition: background-color 0.2s ease;
}

.summary-value {
   color: #9f1e15;
}

.dark-layout .summary-card {
   background: rgba(159, 30, 21, 0.2);
}

.dark-layout .summary-value {
   color: #ff9f94;
}

.kbli-table :deep(.table) {
   margin-bottom: 0;
}

.kbli-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}
</style>
